import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useLayoutEffect, useRef, useState } from 'react';
import landscopeLogo from '../assets/landscope-banner.svg';
// Title block rendered in the print sheet's bottom band (Option B). Holds the
// non-map furniture: title, subtitle, description (left), and the projection +
// brand mark (right). The scale bar and north compass now live ON the map (see
// PrintMapOverlay), which frees this band for a roomier description.
//
// EXPORT CLIP SLACK: every text box here that sets `overflow: hidden` (needed
// for its ellipsis) MUST also carry EXPORT_CLIP_SLACK as padding-bottom. Keep
// both that and the explicit line-height when editing.
//
// html2canvas (the PDF exporter) doesn't lay text out. It takes the element's
// real line box top, then adds a baseline offset measured from a throwaway
// probe (html2canvas.js:6589, `img.offsetTop - span.offsetTop + 2`) and calls
// fillText. That hardcoded +2, plus rounding, draws every glyph a CONSTANT
// ~2-3px lower than the browser does, and `overflow: hidden` clips to the
// PADDING BOX (html2canvas.js:6149) — so the bottom of the type is sheared off
// in the PDF while the on-screen preview looks perfect.
//
// line-height alone does not fix this. A 1.5 line box adds half-leading of
// ~0.175 * fontSize below the glyphs, which is PROPORTIONAL to font size while
// the error is CONSTANT — measured against real exports it rescues 18px type
// (3.15px of slack) but not 13px (2.28px) or 12px (2.10px). Hence the constant
// padding below, which is what actually guarantees the clip can't reach the
// glyphs at any size.
const INK = '#2f3027';
const MUTED = '#8a8b7e';
// Bottom padding that keeps the exporter's constant baseline error clear of the
// overflow:hidden clip (see the note above). Comfortably over the measured
// ~2-3px, and small enough not to disturb the band's vertical rhythm.
const EXPORT_CLIP_SLACK = 4;
const DESCRIPTION_FONT_SIZE = 12;
const DESCRIPTION_LINE_HEIGHT = DESCRIPTION_FONT_SIZE * 1.5;
const DESCRIPTION_MAX_LINES = 4;
const PrintTitleBlock = ({ title, subtitle, description, onDescriptionFit }) => {
    const titleText = title || 'Untitled map';
    const titleFontSize = titleText.length <= 18 ? 18 : titleText.length <= 28 ? 16 : titleText.length <= 40 ? 14 : 13;
    // The band is a fixed fraction of the sheet while the type is fixed-size, so
    // the room left under the title and subtitle varies with paper, orientation
    // and screen. Measure it and cap the description to the lines that fit.
    const columnRef = useRef(null);
    const descriptionRef = useRef(null);
    const [shownLines, setShownLines] = useState(DESCRIPTION_MAX_LINES);
    useLayoutEffect(() => {
        const column = columnRef.current;
        const desc = descriptionRef.current;
        if (!column || !desc) {
            onDescriptionFit === null || onDescriptionFit === void 0 ? void 0 : onDescriptionFit({ shownLines: 0, totalLines: 0 });
            return;
        }
        const measure = () => {
            const room = column.clientHeight - desc.offsetTop - EXPORT_CLIP_SLACK;
            const fits = Math.max(0, Math.min(DESCRIPTION_MAX_LINES, Math.floor(room / DESCRIPTION_LINE_HEIGHT)));
            const total = Math.round((desc.scrollHeight - EXPORT_CLIP_SLACK) / DESCRIPTION_LINE_HEIGHT);
            setShownLines(fits);
            onDescriptionFit === null || onDescriptionFit === void 0 ? void 0 : onDescriptionFit({ shownLines: Math.min(fits, total), totalLines: total });
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(column);
        return () => ro.disconnect();
    }, [title, subtitle, description, titleFontSize, onDescriptionFit]);
    return (_jsxs("div", { style: {
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'space-between',
            gap: 18,
            padding: '9px 18px',
            fontFamily: 'Helvetica, Arial, sans-serif',
        }, children: [_jsxs("div", { ref: columnRef, style: { flex: 1, minWidth: 0, position: 'relative' }, children: [_jsx("div", { style: {
                            fontWeight: 700,
                            color: INK,
                            fontSize: titleFontSize,
                            lineHeight: 1.5,
                            paddingBottom: EXPORT_CLIP_SLACK,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }, children: titleText }), subtitle ? (_jsx("div", { style: {
                            color: INK,
                            fontSize: 13,
                            lineHeight: 1.5,
                            marginTop: 2,
                            paddingBottom: EXPORT_CLIP_SLACK,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }, children: subtitle })) : null, description ? (_jsx("div", { ref: descriptionRef, style: {
                            color: MUTED,
                            fontSize: DESCRIPTION_FONT_SIZE,
                            marginTop: 3,
                            lineHeight: 1.5,
                            paddingBottom: EXPORT_CLIP_SLACK,
                            // Whole measured lines, plus the slack — box-sizing is border-box
                            // here, so without adding it back the padding would eat the last
                            // line. Hidden outright when not even one line fits.
                            maxHeight: shownLines * DESCRIPTION_LINE_HEIGHT + EXPORT_CLIP_SLACK,
                            visibility: shownLines > 0 ? 'visible' : 'hidden',
                            overflow: 'hidden',
                            whiteSpace: 'pre-wrap', // honour the user's line breaks
                        }, children: description })) : null] }), _jsxs("div", { style: {
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                }, children: [_jsxs("div", { style: { fontSize: 11, lineHeight: 1.5, color: MUTED, textAlign: 'right' }, children: ["WGS 84", _jsx("br", {}), "EPSG:4326"] }), _jsx("img", { src: landscopeLogo, alt: "LandScope", style: { height: 36, width: 'auto', display: 'block' } })] })] }));
};
export default PrintTitleBlock;
